import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { AttachedCommunication } from "@/Common/Models/AttachedCommunication/AttachedCommunication";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { DocumentUIState, RedirectTo, TreeNodeTarget } from "@/Common/Models/UI/Enums";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { MedicalDocumentItem } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { MedicalDocumentTableItem } from "@/Documents/Models/Medical/MedicalDocumentTableItem";
import { DocumentsStore } from "@/Documents/Stores/DocumentsStore";

class DocumentMiscHandler extends BaseOperationHandler {
    private readonly helper: MyleHelper;

    constructor() {
        super();

        this.helper = new MyleHelper();

        this.RegisterOperation(LocalOperations.Documents.Attachments_Load, this.attachmentsLoad, false);
        this.RegisterOperation(LocalOperations.Documents.Mark_Document_ForApproval, this.markDocumentForApproval);
        this.RegisterOperation(LocalOperations.Documents.Mark_All_Document_ForApproval, this.markAllDocumentsForApproval);
        this.RegisterOperation(LocalOperations.Documents.Approve, this.approveDocument);
        this.RegisterOperation(LocalOperations.Documents.Tree_SubClass_Selection, this.subclassSelection);
        this.RegisterOperation(LocalOperations.Documents.MultiFile_Window_Resize, this.onWindowResized);
        this.RegisterOperation(LocalOperations.Documents.View_ShowNewNotesControl, this.showNewNotesControl);
        this.RegisterOperation(LocalOperations.Documents.View_HideNewNotesControl, this.hideNewNotesControl);
        this.RegisterOperation(LocalOperations.Documents.ExpandLeftMenu, this.expandLeftMenu);
        this.RegisterOperation(LocalOperations.Documents.CollapseLeftMenu, this.collapseLeftMenu);
        this.RegisterOperation(LocalOperations.Documents.DisplayDocument, this.displayDocument);
        this.RegisterOperation(LocalOperations.Documents.ReturnToSearch, this.returnToSearch);
        this.RegisterOperation(LocalOperations.Documents.View_UnapproveClick, this.openCloseUnapprovePopup);
        this.RegisterOperation(LocalOperations.Documents.View_ConfirmUnapprove, this.confirmUnapprove);
        this.RegisterOperation(LocalOperations.Documents.View_TrailClick, this.trailClick);
        this.RegisterOperation(LocalOperations.Documents.View_AttachedCommunicationClick, this.attachedCommunicationClick);
        this.RegisterOperation(LocalOperations.Documents.View_AttachedCommunicationClose, this.attachedCommunicationClose);
    }

    private attachmentsLoad(request: LocalEvent, store: DocumentsStore): void {
        let tableTarget: MyleTableTarget;
        tableTarget = request.Get<MyleTableTarget>("target");
        switch (tableTarget) {
            case MyleTableTarget.Myle_Documents_SearchResults_RowClick:
                var medicalDocumentItem = request.Get<MedicalDocumentItem>("rowData");
                var docModel = medicalDocumentItem.Model;

                if (docModel) {
                    if (docModel.DocumentPathList.length === 1) {
                        const readableFileName = this.helper.GetReadableFileName(docModel.DocumentPathList[0].FileName);
                        const docUrl = apiSettings.buildAppUrl(`api/medicalDocuments/downloadFile/${docModel.DocumentPathList[0].Id}/${encodeURIComponent(readableFileName)}`, { auth: true, noCache: docModel.IsAnnotated });
                        const secondWindow = MyleHelper.OpenNewWindow(docUrl);
                        store.DocumentsModel = store.DocumentsModel.set("DocumentWindowToBeClosedOnLogOut", secondWindow);
                    }
                    else if (docModel.DocumentPathList.length > 1) {
                        let event = request.Get<React.MouseEvent>("event");
                        store.DocumentsModel = store.DocumentsModel.set("TargetElement", event.nativeEvent.target as HTMLElement);
                        store.DocumentsModel = store.DocumentsModel.set("CurrentAttachements", Immutable.List(docModel.DocumentPathList));
                        store.DocumentsModel = store.DocumentsModel.set("MulitFilePopupOpen", true);
                        store.DocumentsModel = store.DocumentsModel.set("TopPosition", event.clientY);
                        store.DocumentsModel = store.DocumentsModel.set("LeftPosition", event.clientX - 350);
                        sessionStore.showGlobalOverlay();
                        store.EmitChangeToListeners();
                    }
                }
                break;

            default:
                break;
        }
    }

    private markDocumentForApproval(request: LocalEvent, store: DocumentsStore): void {
        let document = request.Get<MedicalDocumentTableItem>("Document");

        let newDocs: MedicalDocumentTableItem[] = store.DocumentsModel.get("DocumentsTableData").toArray().map(x => {
            if (x.Model.Model.Id === document.Model.Model.Id) { x.IsSelected = !x.IsSelected; }
            return x;
        });
        store.DocumentsModel = store.DocumentsModel.set("DocumentsTableData", Immutable.List(newDocs));
    }

    private markAllDocumentsForApproval(request: LocalEvent, store: DocumentsStore): void {
        let valueToSet = request.Get<boolean>("SelectAllValue");
        let newDocs: MedicalDocumentTableItem[] = store.DocumentsModel
            .get("DocumentsTableData")
            .toArray()
            .map(doc => ({
                ...doc,
                IsSelected: doc.Model.Model.CanBeApprovedByCurrentUser ? valueToSet : doc.IsSelected
            }));
        store.DocumentsModel = store.DocumentsModel.set("DocumentsTableData", Immutable.List<MedicalDocumentTableItem>(newDocs));
    }

    private approveDocument(request: LocalEvent, store: DocumentsStore): void {
        let docIDs = request.Get<string[]>("DocumentIDs");
        store.DocumentActions.ApproveDocuments(docIDs, SubServerOperations.Documents.ApproveFromGrid);
    }

    private subclassSelection(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("SelectedView", "allDocuments");
        let treeNodeTarget: TreeNodeTarget;
        treeNodeTarget = request.Get<TreeNodeTarget>("target");
        switch (treeNodeTarget) {
            case TreeNodeTarget.Myle_Documents_DocumentTree_SubClassSelection:
                let docClassId = request.Get<string>("parentId");
                let subClassId = request.Get<string>("childId");
                if (docClassId && subClassId) {
                    // fetch selected docClass/subclass and set as selected in model
                    let documentClass: DocumentClassModel = store.DocumentsModel.get("DocumentClasses").get(docClassId);

                    let documentSubClass: DocumentClassModel = (documentClass && documentClass.SubClasses) ? documentClass.SubClasses.get(subClassId) : null;
                    if (documentClass) {
                        store.DocumentsModel = store.DocumentsModel.set("SelectedDocumentClass", documentClass);
                    }
                    if (documentSubClass) {
                        store.DocumentsModel = store.DocumentsModel.set("SelectedDocumentSubclass", documentSubClass);
                    }
                }
                break;
            default:
                break;
        }
    }

    private displayDocument(request: LocalEvent, store: DocumentsStore): void {
        const docId = request.Get<string>("docId");
        const annotationView = request.Get<boolean>("annotationView");
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocumentId", docId);
        store.DocumentActions.LoadDocument(docId, annotationView);
    }

    private onWindowResized(request: LocalEvent, store: DocumentsStore): void {
        let t = store.DocumentsModel.get("TargetElement");
        if (t != null) {
            t = document.getElementById(t.getAttribute("id"));
            let rect = t.getBoundingClientRect();
            let brect = document.body.getBoundingClientRect();
            let top = rect.top - brect.top;
            let left = rect.left - brect.left;
            store.DocumentsModel = store.DocumentsModel.set("TopPosition", top - 41);
            store.DocumentsModel = store.DocumentsModel.set("LeftPosition", left - 575);
        }
    }

    private showNewNotesControl(request: LocalEvent, store: DocumentsStore): void {
        let doc = store.DocumentsModel.get("SelectedDocument");
        doc = doc.set("ShowNoteCreateForm", true);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", doc);
    }

    private hideNewNotesControl(request: LocalEvent, store: DocumentsStore): void {
        let doc = store.DocumentsModel.get("SelectedDocument");
        doc = doc.set("ShowNoteCreateForm", false);
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", doc);
    }

    private expandLeftMenu(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("LeftMenuOpen", true);
    }

    private collapseLeftMenu(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("LeftMenuOpen", false);
    }

    private returnToSearch(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("UIState", DocumentUIState.Search);
        store.DocumentActions.SearchDocuments(store.DocumentsModel);
    }

    private openCloseUnapprovePopup(request: LocalEvent, store: DocumentsStore): void {
        let doc = store.DocumentsModel.get("SelectedDocument");
        doc = doc.set("UnapprovePopupOpen", !doc.get("UnapprovePopupOpen"));
        store.DocumentsModel = store.DocumentsModel.set("SelectedDocument", doc);
        if (doc.get("UnapprovePopupOpen")) {
            sessionStore.showGlobalOverlay(true);
        }
        else {
            sessionStore.hideGlobalOverlay();
        }
    }

    private confirmUnapprove(request: LocalEvent, store: DocumentsStore): void {
        let doc = request.Get<MedicalDocumentItem>("document");
        let needToRevokeSharedItem = request.Get<boolean>("needToRevokeSharedItem");
        const then = request.Get<any>("then");

        if (needToRevokeSharedItem) {
            store.transformDocumentToSharedItems(doc);
            window.setTimeout(() => {
                sessionStore.dispatch(LocalOperations.Documents.PortalRevokedClicked);
            }, 200);
        }

        store.DocumentActions.unapproveDocument(doc.Model.Id, then);
    }

    private trailClick(request: LocalEvent, store: DocumentsStore): void {
        let eventY = request.Get<number>("eventY");
        let eventX = request.Get<number>("eventX");

        store.DocumentsModel = store.DocumentsModel
            .set("AttachedCommunicationsPopupY", eventY)
            .set("AttachedCommunicationsPopupX", eventX - 600)
            .set("AttachedCommunicationsPopupOpen", true);
    }

    private attachedCommunicationClick(request: LocalEvent, store: DocumentsStore): void {
        let attachedCommunication = request.Get<AttachedCommunication>("attachedCommunication");

        window.setTimeout(() => {
            sessionStore.dispatch(
                LocalOperations.Misc.RedirectToTab,
                {
                    value: attachedCommunication.PatientContactId,
                    currentLocation: RouterHistory.location,
                    taskId: attachedCommunication.IndividualMessageId,
                    target: RedirectTo.Patient
                }
            );
        }, 100);

        store.DocumentsModel = store.DocumentsModel.set("AttachedCommunicationsPopupOpen", false);
    }

    private attachedCommunicationClose(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("AttachedCommunicationsPopupOpen", false);
        store.EmitChangeToListeners();
    }
}

export { DocumentMiscHandler };
